import {
  HttpClient,
  HttpClientError,
  HttpClientRequest,
  type HttpClientResponse,
} from 'effect/http';
import { HttpApiClient, HttpApiMiddleware } from 'effect/http-api';
import { Atom, AtomHttpApi } from 'effect/reactivity';
import {
  Context,
  Data,
  Duration,
  Effect,
  Layer,
  Predicate,
  Schedule,
} from 'effect';
import { ApiHttpApiContract } from './api-contract';
import { statusRetryClass } from './errors';
import { ApiConnectionMiddleware, ApiUnreachableError } from './middleware';

/**
 * How a read is retried when it got no answer or a "try later" status: sent
 * `times` more, the first after `delay` (jittered), each later one doubling
 * it. A write is sent once: the server may have acted on one whose answer was
 * lost. Code that owns its retries, such as the chunk fetcher, runs its calls
 * with `times: 0` so they are not retried twice over.
 */
export const ApiReadRetry = Context.Reference<{
  readonly times: number;
  readonly delay: Duration.Input;
}>('@listening/api-contract/ApiReadRetry', {
  defaultValue: () => ({ times: 1, delay: Duration.millis(500) }),
});

/** Runs `effect`'s API calls without retrying their reads. */
export const withoutApiReadRetry = Effect.provideService(ApiReadRetry, {
  times: 0,
  delay: Duration.zero,
});

class TryLater extends Data.TaggedError('TryLater')<{
  readonly response: HttpClientResponse.HttpClientResponse;
}> {}

const isTransportError = (error: HttpClientError.HttpClientError) =>
  Predicate.isTagged(error.reason, 'TransportError');

const isTryLater = (response: HttpClientResponse.HttpClientResponse) =>
  statusRetryClass(response.status) === 'transient';

const isFromApi = (response: HttpClientResponse.HttpClientResponse) =>
  response.headers['content-type']?.startsWith('application/json') === true;

const unreachable = (
  request: HttpClientRequest.HttpClientRequest,
  response: HttpClientResponse.HttpClientResponse,
) =>
  new ApiUnreachableError({
    cause: new HttpClientError.HttpClientError({
      reason: new HttpClientError.StatusCodeError({ request, response }),
    }),
  });

/** The client half of `ApiConnectionMiddleware`, which every app client of
 * the contract provides. It runs in the caller's fiber, so it reads the
 * caller's `ApiReadRetry`. */
export const apiConnectionClient = HttpApiMiddleware.layerClient(
  ApiConnectionMiddleware,
  ({ request, next }) =>
    Effect.gen(function* () {
      const retry = yield* ApiReadRetry;
      return yield* next(request).pipe(
        Effect.filterOrFail(
          (response) => !isTryLater(response),
          (response) => new TryLater({ response }),
        ),
        Effect.retry({
          times: request.method === 'GET' ? retry.times : 0,
          schedule: Schedule.exponential(retry.delay).pipe(Schedule.jittered),
          while: (error) =>
            Predicate.isTagged(error, 'TryLater') || isTransportError(error),
        }),
        Effect.catchTags({
          TryLater: ({ response }) =>
            isFromApi(response)
              ? Effect.succeed(response)
              : Effect.fail(unreachable(request, response)),
          HttpClientError: (error) =>
            Effect.fail(
              isTransportError(error)
                ? new ApiUnreachableError({ cause: error })
                : error,
            ),
        }),
      );
    }),
);

/** A client of the contract over the provided `HttpClient`, with the
 * connection handling every client of it carries. */
export const makeApiClient = (
  options?: Parameters<typeof HttpApiClient.make>[1],
) =>
  HttpApiClient.make(ApiHttpApiContract, options).pipe(
    Effect.provide(apiConnectionClient),
  );

/** Where an app's API requests go and what they carry. Without a `baseUrl`,
 * requests are relative, which a browser resolves against its own origin. */
export type ApiTransportOptions = {
  readonly baseUrl?: string;
  readonly transformClient?: (
    client: HttpClient.HttpClient,
  ) => HttpClient.HttpClient;
};

/** The `HttpClient` an app's API requests go through: the plain one it
 * provides, sent to `baseUrl` with `transformClient`'s headers. Other plain
 * HTTP, such as doc-pack range reads, keeps the plain one. */
export const apiTransport = (options: ApiTransportOptions = {}) =>
  Layer.effect(
    HttpClient.HttpClient,
    Effect.map(HttpClient.HttpClient, (client) => {
      const routed =
        options.baseUrl === undefined
          ? client
          : HttpClient.mapRequest(
              client,
              HttpClientRequest.prependUrl(options.baseUrl),
            );
      return options.transformClient?.(routed) ?? routed;
    }),
  );

const missingTransport: Layer.Layer<HttpClient.HttpClient> =
  Layer.effectContext(
    Effect.die(
      'The atom registry was not seeded with an API transport, so ApiClient atoms cannot send requests.',
    ),
  );

/** The transport `ApiClient`'s atoms send through. Each app seeds it in its
 * registry with `Atom.initialValue`. */
export const apiTransportAtom = Atom.make(missingTransport).pipe(
  Atom.keepAlive,
);

/**
 * The contract's typed client, and its atoms: `ApiClient.query` and
 * `ApiClient.mutation` take their types and errors from the contract and send
 * through the registry's `apiTransportAtom`.
 */
export class ApiClient extends AtomHttpApi.Service<ApiClient>()(
  '@listening/api-contract/ApiClient',
  {
    api: ApiHttpApiContract,
    httpClient: (get) =>
      Layer.provideMerge(apiConnectionClient, get(apiTransportAtom)),
  },
) {
  /** The client as a service, for runtimes that call it outside atoms. */
  static readonly layer = (
    options: ApiTransportOptions &
      Pick<
        NonNullable<Parameters<typeof HttpApiClient.make>[1]>,
        'transformResponse'
      > = {},
  ) =>
    Layer.effect(
      this,
      makeApiClient({ transformResponse: options.transformResponse }),
    ).pipe(Layer.provide(apiTransport(options)));
}
